import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Flex,
  Box,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Link,
  Button,
  Heading,
  Text,
  Image,
  Switch,
  useColorModeValue,
  FormErrorMessage,
  Center,
  useToast,
  InputGroup,
  InputRightElement,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  VStack,
  Divider,
  HStack,
  Icon,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { ViewIcon, ViewOffIcon, SettingsIcon } from "@chakra-ui/icons";
import { FaMicrosoft } from "react-icons/fa";

import { appRequest } from "routes";
import MicrosoftAuth from "./microsoftLoginpage/page";
import { BASE_PATH } from "utils/env";

type LoginFormValues = {
  email: string;
  password: string;
};

type LoginFormErrors = {
  email?: string;
  password?: string;
};

export default function Login() {
  const blink = keyframes`
    0% { opacity: 1; border-color: #FBD38D; box-shadow: 0 0 0px transparent; }
    50% { opacity: 0.8; border-color: #F56565; box-shadow: 0 0 20px rgba(245, 101, 101, 0.4); }
    100% { opacity: 1; border-color: #FBD38D; box-shadow: 0 0 0px transparent; }
  `;

  const [sapResult, setSapResult] = useState<any>({})
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const Navigate = useNavigate();
  const [values, setValues] = useState<LoginFormValues>({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState<LoginFormErrors>({});

  // Added: Toggle state for password visibility
  const [showPassword, setShowPassword] = useState(false);

  // Get process from URL query params
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const processParam = queryParams.get("process");
  const isForeign = processParam === "foreign";

  const { isOpen, onOpen, onClose } = useDisclosure();

  // Theme-aware colors (top-level hooks)
  const bg = useColorModeValue("gray.50", "gray.800");
  const cardBg = useColorModeValue("white", "gray.700");
  const highlightBg = useColorModeValue("teal.400", "teal.500");
  const subTextColor = useColorModeValue("gray.400", "gray.800");

  useEffect(() => {
    if (isForeign) {
      sessionStorage.setItem("process", "foreign");
    }
    else {
      sessionStorage.removeItem('process')
    }
  }, [isForeign])

  const validateEmail = () => {
    if (!values.email) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: "Email is required",
      }));
    } else if (!/\S+@\S+\.\S+/.test(values.email)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: "Invalid email format",
      }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, email: undefined }));
    }
  };

  const validatePassword = () => {
    if (!values.password) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        password: "Password is required",
      }));
    } else if (values.password.length < 6) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        password: "Password must be at least 6 characters long",
      }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, password: undefined }));
    }
  };


  useEffect(() => {
    async function fetchSapUserStatus() {
      let dd: any = await appRequest("auth", "fetchSapUserStatus", {})
      setSapResult(dd?.data);
    }
    fetchSapUserStatus()
  }, [])

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    validateEmail();
    validatePassword();
    if (!errors.email && !errors.password) {
      // Submit form
      let input = {
        username: values.email,
        password: values.password,
      };
      try {
        setLoading(true);
        const response: any = await appRequest("auth", "login", input);
        if (response) {
          const { token } = response;
          if (!token) {
            toast({
              title: response.message,
              status: "error",
              duration: 5000,
              isClosable: true,
              position: "top-right",
            });
            return;
          } else {
          }
          sessionStorage.setItem("auth-token", token);
          window.location.href = BASE_PATH;
        }
      } catch (error: any) {
        toast({
          title: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-left",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setValues((prevValues) => ({ ...prevValues, [name]: value }));
  };

  console.log("sapResult", sapResult)

  return (
    <Flex
      minH={"100vh"}
      align={"center"}
      justify={"center"}
      bg={bg}
    >
      <Stack spacing={2} mx={"auto"} w={"full"} maxW={"3xl"} py={12} px={6}>
        {sapResult?.sap === false && (
          <Box
            bg="orange.50"
            border="1px solid"
            borderColor="orange.200"
            p={4}
            borderRadius="xl"
            mb={6}
            boxShadow="sm"
            animation={`${blink} 1.5s infinite ease-in-out`}
          >
            <Flex align="center">
              <Box
                bg="orange.400"
                color="white"
                borderRadius="full"
                p={1.5}
                mr={3}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <SettingsIcon boxSize={3} />
              </Box>
              <Text color="orange.800" fontWeight="600" fontSize="sm">
                {sapResult?.sapUpdateMessage}
              </Text>
            </Flex>
          </Box>
        )}
        <Stack align={"center"} spacing={10} mb={isForeign ? 4 : 12}>
          {isForeign ? (
            <>
              <Flex align="center" justify="center" gap={{ base: 4, md: 4 }} w="full">
                <Image
                  src={"/img/gennova.jpg"}
                  h="100px"
                  objectFit="contain"
                  borderRadius="md"
                  display={{ base: "none", md: "block" }}
                />
                <Image src={"/img/emcure_logo.png"} w="280px" />
                <Image
                  src={"/img/zuventus.jpg"}
                  h="100px"
                  objectFit="contain"
                  borderRadius="md"
                  display={{ base: "none", md: "block" }}
                />
              </Flex>

              <Box
                px={12}
                py={3}
                bg={highlightBg}

                borderRadius="full"
                boxShadow="0 10px 30px -5px rgba(56, 178, 172, 0.4)"
                border="2px solid"
                borderColor="white"
                position="relative"
                transition="all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)"
                _hover={{ transform: "translateY(-4px)", boxShadow: "0 15px 40px -8px rgba(56, 178, 172, 0.5)" }}
              >
                <Text
                  fontSize="sm"
                  fontWeight="900"
                  color="white"
                  letterSpacing="5px"
                  textTransform="uppercase"
                  textAlign="center"
                  ml="5px"
                >
                  foreign payments VIM Portal
                </Text>
              </Box>
            </>
          ) : (
            <Image src={"/img/emcure_logo.png"} w="240px" />
          )}
        </Stack>



        <Box w="full" maxW="lg" mx="auto">
          <IconButton
            icon={<SettingsIcon />}
            variant="ghost"
            position="fixed"
            top={4}
            right={4}
            onClick={onOpen}
            aria-label="Admin Login"
            _hover={{ bg: useColorModeValue("gray.200", "gray.600") }}
          />

          <Box
            rounded={"2xl"}
            bg={cardBg}
            boxShadow={"2xl"}
            p={{ base: 6, md: 10 }}
            position="relative"
            overflow="hidden"
            border="1px solid"
            borderColor={useColorModeValue("gray.100", "gray.700")}
            transition="all 0.3s ease"
            _hover={{ boxShadow: "3xl", borderColor: "teal.200" }}
          >
            {/* Decorative background element */}
            <Box
              position="absolute"
              top="-10%"
              right="-10%"
              w="150px"
              h="150px"
              bg="teal.50"
              borderRadius="full"
              filter="blur(60px)"
              opacity={0.6}
              zIndex={0}
            />
            <Stack spacing={8} position="relative" zIndex={1}>
              <VStack spacing={2}>
                <Heading
                  color="teal.400"
                  fontSize={{ base: "3xl", md: "4xl" }}
                  fontWeight="800"
                  textAlign="center"
                  letterSpacing="tight"
                >
                  Welcome Back
                </Heading>
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color="gray.500"
                  fontWeight="500"
                  textAlign="center"
                  maxW="xs"
                >
                  Securely sign in to your project workspace using your Microsoft account
                </Text>
              </VStack>

              {sapResult?.sap === false ? (
                <>
                  <Button
                    w="full"
                    variant="outline"
                    isDisabled
                    leftIcon={<Icon as={FaMicrosoft} />}
                    rounded="lg"
                    fontWeight="500"
                    fontSize="sm"
                    py={5}
                    _disabled={{
                      opacity: 0.6,
                      cursor: "not-allowed",
                      bg: "gray.50",
                    }}
                  >
                    Sign in with Microsoft
                  </Button>
                </>
              ) : (
                <Box pt={2}>
                  <MicrosoftAuth />
                </Box>
              )}

              <HStack w="full" py={2}>
                <Divider />
                <Text fontSize="xs" fontWeight="bold" color="gray.400" whiteSpace="nowrap" px={2} textTransform="uppercase" letterSpacing="widest">
                  Secure Access
                </Text>
                <Divider />
              </HStack>

              <Text fontSize="sm" color="gray.400" textAlign="center">
                For any assistance - reach out to your VIM {isForeign ? "Foriegn Payment Admin" : "Admin"}{" "}

                {isForeign && (
                  <>
                    <Text as="span" fontWeight="semibold" color="gray.600">
                      <br />Mr. Amit Jadhav (Treasury Department)<br /></Text>{" "}
                  </>
                )}

                at{" "}

                <Text textDecoration="underline" as="span" fontWeight="semibold" color="gray.600">
                  {isForeign
                    ? "foreign.invoicing@emcure.com"
                    : "Anurag.Kautilya@emcure.com"}
                </Text>{"          "}
              </Text>
            </Stack>
          </Box>

          <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
            <ModalOverlay backdropFilter="blur(4px)" />
            <ModalContent borderRadius="xl" p={2}>
              <ModalHeader>
                <VStack align="center" spacing={1}>
                  <Text color="teal.300">Traditional Sign In</Text>
                  <Text fontSize="sm" fontWeight="normal" color="gray.500">
                    Login with email and password
                  </Text>
                </VStack>
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody pb={6}>
                <form onSubmit={handleSubmit}>
                  <Stack spacing={4}>
                    <FormControl id="email" isInvalid={!!errors.email}>
                      <FormLabel color="gray.700">Email</FormLabel>
                      <Input
                        type="email"
                        placeholder="Your email address"
                        name="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={validateEmail}
                      />
                      <FormErrorMessage>{errors.email}</FormErrorMessage>
                    </FormControl>

                    <FormControl id="password" isInvalid={!!errors.password}>
                      <FormLabel color="gray.700">Password</FormLabel>
                      <InputGroup>
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="Your password"
                          name="password"
                          value={values.password}
                          onChange={handleChange}
                          onBlur={validatePassword}
                        />
                        <InputRightElement>
                          <IconButton
                            aria-label={
                              showPassword ? "Hide password" : "Show password"
                            }
                            icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                            size="sm"
                            onClick={() => setShowPassword(!showPassword)}
                            variant="ghost"
                            _hover={{ bg: "transparent" }}
                          />
                        </InputRightElement>
                      </InputGroup>
                      <FormErrorMessage>{errors.password}</FormErrorMessage>
                    </FormControl>

                    <Button
                      type="submit"
                      bg="teal.500"
                      h={"50px"}
                      fontSize="md"
                      fontWeight="800"
                      color="white"
                      mt={6}
                      rounded="xl"
                      _hover={{
                        bg: "teal.600",
                        transform: "translateY(-1px)",
                        boxShadow: "lg",
                      }}
                      _active={{ transform: "translateY(0px)" }}
                      isLoading={loading}
                      textTransform="uppercase"
                      letterSpacing="wider"
                    >
                      Sign In Now
                    </Button>
                  </Stack>
                </form>
              </ModalBody>
            </ModalContent>
          </Modal>


          <Stack align="center" spacing={2} mt={12}>


            <Text
              fontSize="sm"
              color="gray.500"
              fontWeight="600"
              letterSpacing="0.5px"
            >
              Powered By
            </Text>

            <Image
              src="/img/sequelstring.webp"
              alt="SequelString Logo"
              width="240px"
              objectFit="contain"
              opacity={0.9}
              _hover={{ opacity: 1, transform: "scale(1.03)" }}
              transition="0.2s ease"
            />
            {isForeign && (
              <Text mt={3} fontSize="xs" color="gray.500" mb={1} fontWeight="600">
                Developed by Amit Jadhav and Anurag Kautilya
              </Text>
            )}
          </Stack>
        </Box>
      </Stack>
    </Flex>
  );
}